import React, { useState } from 'react';
import axios from 'axios';
import Botpoison from '@botpoison/browser';
import './BrokerCompensation.css';

// Create a new Botpoison instance with the provided public key
const botpoison = new Botpoison({
  publicKey: "pk_24272137-5952-47c8-85cd-c52f3355afef"
});

const BrokerCompensation: React.FC = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    companyName: '',
    email: '',
    additionalInfo: '',
    privacy: false
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFormData({
      ...formData,
      [name]: checked
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.privacy) {
      alert('Please agree to the privacy policy to submit the form.');
      return;
    }

    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      // Process Botpoison challenge
      const { solution } = await botpoison.challenge();

      // Submit to Formspark
      const response = await axios.post('https://submit-form.com/mZtNOyrxm', {
        firstName: formData.firstName,
        lastName: formData.lastName,
        companyName: formData.companyName,
        email: formData.email,
        additionalInfo: formData.additionalInfo,
        privacy: formData.privacy,
        _botpoison: solution, // Forward the Botpoison solution
        _subject: 'Broker Commission Plan Information Request'
      });

      if (response.status === 200) {
        setSubmitMessage('Thank you! Your request has been submitted successfully. An SC Properties team member will reach out to you shortly with detailed information about our broker commission plan.');
        setFormData({
          firstName: '',
          lastName: '',
          companyName: '',
          email: '',
          additionalInfo: '',
          privacy: false
        });
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitMessage('There was an error submitting your request. Please try again or contact us directly at smg@scpropsm.com.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="broker-compensation" id="broker-compensation">
      <div className="container">
        <h2>Licensed Broker Commission Plan</h2>
        <div className="broker-content">
          <div className="broker-form-section">
            <p className="intro-text">
              Complete the form below to receive comprehensive information about our broker commission plan. 
              An SC Properties team member will reach out to provide detailed commission structures, terms, 
              and partnership opportunities for licensed brokers and referral platforms.
            </p>
            
            <form className="broker-form" onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="firstName">First Name *</label>
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    required
                    placeholder="Your First Name"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="lastName">Last Name *</label>
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    required
                    placeholder="Your Last Name"
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="companyName">Company Name *</label>
                <input
                  type="text"
                  id="companyName"
                  name="companyName"
                  value={formData.companyName}
                  onChange={handleChange}
                  required
                  placeholder="Your Company Name"
                />
              </div>

              <div className="form-group">
                <label htmlFor="email">Email Address *</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  placeholder="Your Email Address"
                />
              </div>

              <div className="form-group">
                <label htmlFor="additionalInfo">Anything Else We Need to Know?</label>
                <textarea
                  id="additionalInfo"
                  name="additionalInfo"
                  value={formData.additionalInfo}
                  onChange={handleChange}
                  rows={4}
                  placeholder="Tell us about your brokerage, referral volume, or any specific questions about our commission plan"
                ></textarea>
              </div>

              <div className="form-group checkbox-group">
                <input
                  type="checkbox"
                  id="privacy"
                  name="privacy"
                  checked={formData.privacy}
                  onChange={handleCheckboxChange}
                  required
                />
                <label htmlFor="privacy">
                  I agree to the privacy policy and consent to being contacted about broker commission opportunities
                </label>
              </div>

              <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                {isSubmitting ? 'Submitting Request...' : 'Request Commission Information'}
              </button>

              {submitMessage && (
                <div className={`submit-message ${submitMessage.includes('error') ? 'error' : 'success'}`}>
                  {submitMessage}
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BrokerCompensation; 